/**
 * Out-of-town travel surcharge when product/variation text matches configured keywords.
 * Server validates amounts with the same rules.
 */

export const PERSONAL_SERVICES_SLUG = 'personal-services';

const OUT_OF_TOWN_KEYWORD_PATTERNS = [/at\s+home/i, /vivaham/i, /marriage/i];

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

export function isPersonalServicesProduct(categorySlugs) {
  const slugs = Array.isArray(categorySlugs) ? categorySlugs : [];
  return slugs.some((s) => String(s || '').trim().toLowerCase() === PERSONAL_SERVICES_SLUG);
}

export function isAtHomeVariationLabel(label) {
  return /at\s+home/i.test(String(label || ''));
}

/** Match product name + variation label (e.g. Vivaham / marriage on product, at home on variation). */
export function lineNeedsOutOfTownQuestion(productName, variationLabel) {
  const text = `${String(productName || '')} ${String(variationLabel || '')}`;
  return OUT_OF_TOWN_KEYWORD_PATTERNS.some((re) => re.test(text));
}

/** @param {string[]} [_categorySlugs] ignored; kept for call-site compatibility */
export function variationNeedsOutOfTownQuestion(_categorySlugs, variationLabel, productName = '') {
  return lineNeedsOutOfTownQuestion(productName, variationLabel);
}

export function normalizeOutOfTownDays(raw, maxDays = 2) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const cap = Number.isFinite(maxDays) && maxDays >= 1 ? maxDays : 2;
  return Math.min(cap, n);
}

export function computeOutOfTownSurcharge(days, dayRate = 251) {
  const d = normalizeOutOfTownDays(days);
  if (d <= 0) return 0;
  const rate = Number(dayRate);
  return round2((Number.isFinite(rate) && rate >= 0 ? rate : 251) * d);
}

export function cartMerchandiseLineTotal(item) {
  const qty = Math.max(1, parseInt(item?.quantity, 10) || 1);
  return round2(Number(item?.unit_price || 0) * qty);
}

export function cartLineOutOfTownSurcharge(item, dayRate = 251) {
  const days = normalizeOutOfTownDays(item?.out_of_town_days);
  if (days <= 0) return 0;
  return computeOutOfTownSurcharge(days, dayRate);
}

export function cartMerchandiseSubtotal(cart) {
  return round2((cart || []).reduce((s, i) => s + cartMerchandiseLineTotal(i), 0));
}

export function cartOutOfTownSubtotal(cart, dayRate = 251) {
  return round2((cart || []).reduce((s, i) => s + cartLineOutOfTownSurcharge(i, dayRate), 0));
}

export function cartCheckoutSubtotal(cart, dayRate = 251) {
  return round2(cartMerchandiseSubtotal(cart) + cartOutOfTownSubtotal(cart, dayRate));
}

export function outOfTownChoiceLabel(days, dayRate = 251) {
  if (days <= 0) return 'Shelby County (no travel surcharge)';
  const amt = computeOutOfTownSurcharge(days, dayRate);
  return days === 1
    ? `Outside Shelby County — 1 day (+$${amt.toFixed(2)})`
    : `Outside Shelby County — ${days} days (+$${amt.toFixed(2)})`;
}

export function mergedOutOfTownUi(...sources) {
  const defaults = { dayRate: 251, maxDays: 2, feeName: 'Outside of Shelby County' };
  const out = { ...defaults };
  for (const s of sources) {
    if (!s || typeof s !== 'object') continue;
    if (Number.isFinite(Number(s.dayRate)) && Number(s.dayRate) >= 0) out.dayRate = Number(s.dayRate);
    if (Number.isFinite(Number(s.maxDays)) && Number(s.maxDays) >= 1) out.maxDays = Number(s.maxDays);
    if (typeof s.feeName === 'string' && s.feeName.trim()) out.feeName = s.feeName.trim();
  }
  return out;
}
